import { supabase } from '@/lib/supabase/client';
import type { UserActivityEvent } from '@/types/userActivity';

/**
 * Fire-and-forget activity logger.
 * Synchronous entry point — calls async internally, never blocks the caller.
 */
export function logUserActivity(event: UserActivityEvent): void {
  _logAsync(event).catch(() => {
    // Silent — activity logging should never surface errors to the user
  });
}

async function _logAsync(event: UserActivityEvent): Promise<void> {
  try {
    const {
      data: { user }
    } = await supabase.auth.getUser();
    if (!user) {
      return;
    }

    const {
      data: { session }
    } = await supabase.auth.getSession();
    const tenantId = session?.access_token
      ? _extractJWTField(session.access_token, 'tenant_id')
      : null;
    const tenantRole = session?.access_token
      ? _extractJWTField(session.access_token, 'tenant_role')
      : null;

    // tenant_id is NOT NULL in the table — bail if unavailable
    if (!tenantId) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase.schema('prod').from('user_activity_log') as any).insert({
      user_id: user.id,
      user_email: user.email || null,
      tenant_id: tenantId,
      tenant_role: tenantRole,
      activity_category: event.category,
      activity_action: event.action,
      source_page: event.sourcePage || window.location.pathname,
      entity_type: event.entityType || null,
      entity_id: event.entityId || null,
      entity_name: event.entityName || null,
      item_count: event.itemCount ?? 1,
      metadata: event.metadata || {}
    });

    if (error) {
      // Silent failure - activity logging should not surface errors
    }
  } catch {
    // Silent failure - activity logging should not surface errors
  }
}

function _extractJWTField(token: string, field: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const claims = JSON.parse(atob(payload));
    return claims[field] || null;
  } catch {
    return null;
  }
}
